// Maps a seller-service listing-request error into per-field messages plus the
// earliest wizard step that owns a failing field, so the pricing-step submit can
// route the user back to the exact place they need to fix. The submit is a single
// multipart call, so a field error for step 2/3 surfaces here at step 4 and must
// be relocated.
//
// Wire error shape (GlobalExceptionHandler): { success, errorCode, message,
// errors: [{ field, errorCode, message }] | null }. Field validation errors ride
// `errors[]`; business rules ride the top-level `errorCode` with `errors: null`.
import type { AppError } from '@core/api/normalizeError'
import { STEP_KEYS, type ListingStepKey } from '../constants/listingRequest'

interface WireFieldError {
  field: string
  errorCode?: string
  message: string
}

interface WireErrorBody {
  errorCode?: string
  message?: string
  errors?: WireFieldError[] | null
}

export interface MappedListingError {
  // FE-field-name → message (owners.* collapse to `owners`).
  serverErrors: Record<string, string>
  // Non-field, step-level message (empty when every error mapped to a field).
  formError: string
  // Earliest wizard step owning a failing field, or null to stay put.
  targetStep: ListingStepKey | null
}

// Which wizard step owns each FE field. BE field names already match FE names;
// nested owner errors (`owners.0.fullName`) collapse to the `owners` key.
const FIELD_TO_STEP: Record<string, ListingStepKey> = {
  ownershipRole: 'ownership',
  titleDeed: 'titleDeed',
  certificateNumber: 'titleDeed',
  propertyName: 'titleDeed',
  isRented: 'titleDeed',
  rentalAgreement: 'titleDeed',
  owners: 'verify',
  propertyType: 'verify',
  propertyTypeOnDeed: 'verify',
  isFreehold: 'verify',
  isResidential: 'verify',
  isMortgageFree: 'verify',
  isDldBlockFree: 'verify',
  acceptedTerms: 'verify',
  totalAreaSqm: 'verify',
  builtUpAreaSqm: 'verify',
  propertyNumber: 'verify',
  buildingName: 'verify',
  locationArea: 'verify',
  addressLine1: 'verify',
  city: 'verify',
  countryId: 'verify',
  plotNumber: 'verify',
  municipalityNumber: 'verify',
  tenantName: 'verify',
  annualRentAmount: 'verify',
  leaseStart: 'verify',
  leaseEnd: 'verify',
  askingPrice: 'pricing',
  currencyCode: 'pricing',
}

// Business-rule error codes (errors: null) → the FE field + step that owns them.
const BUSINESS_CODE_TO_FIELD: Record<string, string> = {
  PROPERTY_TITLE_DEED_REQUIRED: 'titleDeed',
  PROPERTY_TITLE_DEED_INVALID_FORMAT: 'titleDeed',
  FILE_SIZE_TOO_LARGE: 'titleDeed',
  PROPERTY_RENTAL_AGREEMENT_REQUIRED: 'rentalAgreement',
  PROPERTY_RENTAL_AGREEMENT_INVALID_FORMAT: 'rentalAgreement',
  // Reachable only from an API-direct submit or a stale draft — the toggle clears
  // the file client-side — but it still belongs on the field the seller can fix.
  PROPERTY_RENTAL_AGREEMENT_NOT_APPLICABLE: 'rentalAgreement',
  // The rent terms are validated as one declaration server-side, so an incomplete
  // set arrives as a single error rather than one per field. It lands on the tenant
  // name — the first field of the section — so the message is visible next to the
  // block the seller has to complete.
  PROPERTY_RENT_DETAILS_REQUIRED: 'tenantName',
  // Same reachability as the agreement's not-applicable code: the toggle clears the
  // terms client-side, so this only arrives from an API-direct submit or a stale draft.
  PROPERTY_RENT_DETAILS_NOT_APPLICABLE: 'tenantName',
  PROPERTY_RENT_PERIOD_INVALID: 'leaseEnd',
  // PROPERTY_LISTING_REQUEST_DUPLICATE is deliberately NOT mapped to a field. A
  // duplicate (409) is a business rule the seller can't resolve by editing the deed
  // number, so field-routing it bounces them to an earlier step and buries the real
  // message under "Title deed no.". Left unmapped it falls through to the step-level
  // handler, which keeps them on the review screen with the server's text.
  // The client blocks the step on all five too, so reaching here means an API-direct
  // submit or a draft that predates the rules — route each to its own checkbox
  // regardless, since that is where the seller fixes it.
  PROPERTY_FREEHOLD_DECLARATION_REQUIRED: 'isFreehold',
  PROPERTY_RESIDENTIAL_DECLARATION_REQUIRED: 'isResidential',
  PROPERTY_MORTGAGE_DECLARATION_REQUIRED: 'isMortgageFree',
  PROPERTY_DLD_BLOCK_DECLARATION_REQUIRED: 'isDldBlockFree',
  PROPERTY_TERMS_ACCEPTANCE_REQUIRED: 'acceptedTerms',
  // The dropdown only offers deed types under the picked type, so a crossed pair
  // means an API-direct submit or a stale draft; it is fixed on the deed type.
  PROPERTY_TYPE_ON_DEED_MISMATCH: 'propertyTypeOnDeed',
  PROPERTY_OWNERSHIP_TOTAL_INVALID: 'owners',
  PROPERTY_OWNERSHIP_ROLE_MISMATCH: 'owners',
  // Surfaces under the ownership card next to the primary-owner radio, so the
  // message lands where the fix is.
  PROPERTY_OWNERSHIP_PRIMARY_INVALID: 'owners',
  // The client checks this too, so reaching here means either an API-direct submitter
  // or a legacy draft that skipped the client rule — still route it to the owner rows.
  PROPERTY_OWNERSHIP_EMIRATES_ID_DUPLICATE: 'owners',
}

function normalizeFieldName(field: string): string {
  return field.startsWith('owners') ? 'owners' : field
}

function earliestStep(fields: string[]): ListingStepKey | null {
  let best: ListingStepKey | null = null
  let bestIndex = Number.POSITIVE_INFINITY
  for (const field of fields) {
    const step = FIELD_TO_STEP[field]
    if (!step) continue
    const index = STEP_KEYS.indexOf(step)
    if (index >= 0 && index < bestIndex) {
      bestIndex = index
      best = step
    }
  }
  return best
}

const FALLBACK_MESSAGE = 'Something went wrong. Please try again.'

// One-line message for the detail screen's action surfaces (price response,
// withdrawal, decline-to-sign). Those endpoints return a single business error,
// not a field map, so they don't need `mapListingRequestError` below.
export function errorMessageOf(error: unknown): string {
  return (error as AppError | null)?.message || FALLBACK_MESSAGE
}

export function mapListingRequestError(error: AppError): MappedListingError {
  const body = (error.details ?? {}) as WireErrorBody
  const fallbackMessage = error.message || 'Failed to submit your sell request. Please try again.'
  const serverErrors: Record<string, string> = {}

  // 1. Field-level validation errors from `errors[]`.
  if (Array.isArray(body.errors) && body.errors.length > 0) {
    for (const fieldError of body.errors) {
      const key = normalizeFieldName(fieldError.field)
      // Keep the first message per field — owners.* rows collapse to one line.
      if (!serverErrors[key]) serverErrors[key] = fieldError.message
    }
    return {
      serverErrors,
      formError: '',
      targetStep: earliestStep(Object.keys(serverErrors)),
    }
  }

  // 2. Business-rule error mapped to a specific field.
  const businessField = error.errorCode ? BUSINESS_CODE_TO_FIELD[error.errorCode] : undefined
  if (businessField) {
    serverErrors[businessField] = fallbackMessage
    return {
      serverErrors,
      formError: '',
      targetStep: earliestStep([businessField]),
    }
  }

  // 3. Anything else — surface as a step-level message, stay on the current step.
  return { serverErrors: {}, formError: fallbackMessage, targetStep: null }
}
